/**
 * GA4 + Google Consent Mode v2 wrapper.
 *
 * To activate:
 *   1. Replace MEASUREMENT_ID below with your real GA4 ID (G-XXXXXXXXXX).
 *   2. Done. Consent banner + tracking loads automatically.
 */

export const MEASUREMENT_ID = "G-XXXXXXXXXX"; // <-- replace with your real GA4 ID

const CONSENT_KEY = "gpv-cookie-consent-v2"; // JSON: { analytics, marketing, ts, version }
const LEGACY_KEY = "gpv-cookie-consent"; // legacy single-bool key
export const CONSENT_VERSION = 2;

export type ConsentCategories = {
  necessary: true; // always true - strictly necessary cookies are exempt under GDPR/PECR
  analytics: boolean;
  marketing: boolean;
};

export type ConsentRecord = ConsentCategories & {
  ts: number; // epoch ms when the choice was made
  version: number; // increment to re-prompt all users when policy changes
};

declare global {
  interface Window {
    dataLayer: unknown[];
    gtag: (...args: unknown[]) => void;
  }
}

let bootstrapped = false;

function gtag(...args: unknown[]) {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(args);
}

/** Inject GA4 + set Consent Mode v2 defaults to DENIED. Safe to call multiple times. */
export function bootstrapAnalytics() {
  if (bootstrapped) return;
  if (typeof window === "undefined") return;
  if (!MEASUREMENT_ID || MEASUREMENT_ID === "G-XXXXXXXXXX") return;

  bootstrapped = true;
  window.dataLayer = window.dataLayer || [];
  window.gtag = function () {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer.push(arguments);
  };

  // Consent Mode v2 - default everything to denied until the user accepts.
  gtag("consent", "default", {
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
    analytics_storage: "denied",
    functionality_storage: "granted",
    security_storage: "granted",
    wait_for_update: 500,
  });

  gtag("js", new Date());
  gtag("config", MEASUREMENT_ID, {
    anonymize_ip: true,
    send_page_view: false,
  });

  const s = document.createElement("script");
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${MEASUREMENT_ID}`;
  document.head.appendChild(s);

  // Re-apply any saved consent.
  const saved = getConsent();
  if (saved) applyConsentToGtag(saved);
}

function applyConsentToGtag(c: ConsentCategories) {
  gtag("consent", "update", {
    analytics_storage: c.analytics ? "granted" : "denied",
    ad_storage: c.marketing ? "granted" : "denied",
    ad_user_data: c.marketing ? "granted" : "denied",
    ad_personalization: c.marketing ? "granted" : "denied",
  });
}

/** Persist a granular consent choice and push it to GA. */
export function setConsent(categories: Omit<ConsentCategories, "necessary">) {
  const record: ConsentRecord = {
    necessary: true,
    analytics: !!categories.analytics,
    marketing: !!categories.marketing,
    ts: Date.now(),
    version: CONSENT_VERSION,
  };
  try {
    localStorage.setItem(CONSENT_KEY, JSON.stringify(record));
    localStorage.removeItem(LEGACY_KEY);
  } catch {
    /* ignore */
  }
  applyConsentToGtag(record);
  // Notify listeners (e.g. footer "Cookie settings" link) so the UI can react.
  try {
    window.dispatchEvent(new CustomEvent("gpv:consent-changed", { detail: record }));
  } catch {
    /* ignore */
  }
}

/** Get current consent record, or null if the user has not chosen yet. */
export function getConsent(): ConsentRecord | null {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as ConsentRecord;
      if (parsed && parsed.version === CONSENT_VERSION) return parsed;
      // Outdated policy version - treat as no choice so we re-prompt.
      return null;
    }
    // Migrate from legacy key.
    const legacy = localStorage.getItem(LEGACY_KEY);
    if (legacy === "granted" || legacy === "denied") {
      const granted = legacy === "granted";
      const migrated: ConsentRecord = {
        necessary: true,
        analytics: granted,
        marketing: false, // legacy banner never asked about ads - default off
        ts: Date.now(),
        version: CONSENT_VERSION,
      };
      try {
        localStorage.setItem(CONSENT_KEY, JSON.stringify(migrated));
        localStorage.removeItem(LEGACY_KEY);
      } catch {
        /* ignore */
      }
      return migrated;
    }
  } catch {
    /* ignore */
  }
  return null;
}

/** Force the banner to reappear (used by the "Cookie settings" footer link). */
export function reopenConsentBanner() {
  try {
    localStorage.removeItem(CONSENT_KEY);
    localStorage.removeItem(LEGACY_KEY);
    window.dispatchEvent(new CustomEvent("gpv:consent-reopen"));
  } catch {
    /* ignore */
  }
}

/** Track a SPA page view. Call on route change. */
export function trackPageView(path: string, title?: string) {
  gtag("event", "page_view", {
    page_path: path,
    page_title: title ?? document.title,
    page_location: window.location.href,
  });
}

/** Track a custom event. Use names from EVENTS. */
export function trackEvent(name: string, params: Record<string, unknown> = {}) {
  gtag("event", name, params);
}

/**
 * Canonical event names. Matches the events checklist in /docs/analytics-events.md.
 */
export const EVENTS = {
  SEARCH_SUBMIT: "search_submit",
  POSTCODE_SEARCH: "postcode_search",
  PET_CATEGORY_SELECT: "pet_category_select",
  VET_PROFILE_VIEW: "vet_profile_view",
  VET_CARD_CLICK: "vet_card_click",
  VET_CONTACT_CLICK: "vet_contact_click",
  VET_DIRECTIONS_CLICK: "vet_directions_click",
  BOOK_APPOINTMENT_START: "book_appointment_start",
  BOOK_APPOINTMENT_SLOT_SELECTED: "book_appointment_slot_selected",
  BOOK_APPOINTMENT_DETAILS_SUBMITTED: "book_appointment_details_submitted",
  BOOK_APPOINTMENT_SUBMIT: "book_appointment_submit",
  BOOK_APPOINTMENT_SUCCESS: "book_appointment_success",
  SIGN_UP_START: "sign_up_start",
  SIGN_UP_SUCCESS: "sign_up",
  LOGIN_SUCCESS: "login",
  PASSPORT_CTA_CLICK: "passport_cta_click",
  PASSPORT_CREATE: "passport_create",
  PASSPORT_SHARE: "passport_share",
  ADD_PET: "add_pet",
  VIDEO_VET_CTA_CLICK: "video_vet_cta_click",
  VIDEO_CONSULT_REQUEST: "video_consult_request",
  VIDEO_CONSULT_START: "video_consult_start",
  VIDEO_CONSULT_END: "video_consult_end",
  VIDEO_PLAY: "video_play",
  VIDEO_COMPLETE: "video_complete",
  SNIFFER_OPEN: "sniffer_open",
  SNIFFER_SUBMIT: "sniffer_submit",
  SNIFFER_RESULT_VIEW: "sniffer_result_view",
  SNIFFER_BOOK_FROM_RESULT: "sniffer_book_from_result",
  VET_SIGNUP_START: "vet_signup_start",
  VET_SIGNUP_SUBMIT: "vet_signup_submit",
  VET_SUBSCRIBE_CHECKOUT: "vet_subscribe_checkout",
  VET_SUBSCRIBE_SUCCESS: "vet_subscribe_success",
  CONTACT_FORM_SUBMIT: "contact_form_submit",
  HELPDESK_ARTICLE_VIEW: "helpdesk_article_view",
  OUTBOUND_CLICK: "click",
  SHARE: "share",
  GENERATE_LEAD: "generate_lead",
} as const;

export type EventName = (typeof EVENTS)[keyof typeof EVENTS];
